import { PublicReferralCampaign, Referral } from 'model/referralCampaign';
import React, { SetStateAction, Dispatch, createContext } from 'react';
import { useRouter } from 'next/router';
import useApi from 'hooks/useApi';
import useUserReferralCampaigns from 'hooks/api/useUserReferralCampaigns';
import { IFlatAddress } from '@poinz/api';
export interface Props {
  referralCampaign?: PublicReferralCampaign | null;
  children: React.ReactNode;
}

export interface ReferralPageContext {
  referralCampaign?: PublicReferralCampaign | null;
  setReferralCampaign: (campaign: any | null) => void;
  appliedReferralCamp?: Referral;
  setAppliedReferralCamp: Dispatch<SetStateAction<Referral | undefined>>;
  isLoading: boolean;
  applyReferralCampaign: (addressFormData?: IFlatAddress) => Promise<void>;
  userReferralCampaigns: Referral[];
  setIsAddressDialogOpen: (isOpen: boolean) => void;
  isAddressDialogOpen: boolean;
}

const ReferralContext = createContext<ReferralPageContext>({
  referralCampaign: null,
  setReferralCampaign: () => {},
  isLoading: false,
  setAppliedReferralCamp: () => {},
  applyReferralCampaign: () => Promise.resolve(),
  userReferralCampaigns: [],
  setIsAddressDialogOpen: () => {},
  isAddressDialogOpen: false
});

export const useReferralPageContext = (): ReferralPageContext => {
  const contextValue = React.useContext(ReferralContext);

  return contextValue;
};

const ReferralPageProvider = ({ referralCampaign: defaultReferralCampaign, children }: Props) => {
  const [referralCampaign, setReferralCampaign] = React.useState(defaultReferralCampaign);
  const [appliedReferralCamp, setAppliedReferralCamp] = React.useState<Referral>();
  const [isAddressDialogOpen, setIsAddressDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { api } = useApi();
  const router = useRouter();
  const { code } = router.query;
  const { userReferralCampaigns } = useUserReferralCampaigns();

  React.useEffect(() => {
    if (JSON.stringify(defaultReferralCampaign) !== JSON.stringify(referralCampaign)) {
      setReferralCampaign(defaultReferralCampaign);
    }
  }, [defaultReferralCampaign, referralCampaign]);

  const applyReferralCampaign = React.useCallback(
    async (addressFormData?: IFlatAddress) => {
      if (!api) {
        return;
      }

      setIsLoading(true);

      try {
        const appliedReferralCampaign = await api.referral.referrals.apply(
          code as string,
          addressFormData as any
        );
        setAppliedReferralCamp(appliedReferralCampaign as any as Referral);
      } finally {
        setIsLoading(false);
      }
    },
    [api, code]
  );

  const contextValue = {
    referralCampaign,
    setReferralCampaign,
    appliedReferralCamp,
    setAppliedReferralCamp,
    isLoading,
    applyReferralCampaign,
    userReferralCampaigns,
    setIsAddressDialogOpen,
    isAddressDialogOpen
  };

  return <ReferralContext.Provider value={contextValue}>{children}</ReferralContext.Provider>;
};

export default ReferralPageProvider;
